import { useState } from 'react';

interface ContactFormProps {
  onClose: () => void;
}

export default function ContactForm({ onClose }: ContactFormProps) {
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setStatus('sending');

    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => {
      body.append(key, value as string);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d6m7d8c6fojmejgajalg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('sent');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-lg bg-white shadow-2xl"
        style={{ fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 pt-8 pb-6 border-b border-stone-100">
          <div>
            <h2 className="text-xl font-light tracking-widest uppercase text-stone-900">
              Contact
            </h2>
            <p className="text-xs text-stone-400 tracking-wide mt-1 uppercase">
              Joshua Smith · Contemporary Gallery
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-stone-900 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {status === 'sent' ? (
          <div className="px-8 py-12 text-center">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-4 rounded-full bg-stone-100">
              <i className="ri-check-line text-2xl text-stone-700"></i>
            </div>
            <p className="text-stone-800 font-light tracking-wide text-lg mb-2">Message Sent</p>
            <p className="text-stone-400 text-sm tracking-wide">
              Thank you for reaching out. We'll be in touch soon.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-8 px-8 py-3 bg-stone-900 text-white text-xs tracking-widest uppercase font-light hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              Close
            </button>
          </div>
        ) : (
          <form
            data-readdy-form
            id="gallery-contact-form"
            onSubmit={handleSubmit}
            className="px-8 py-6 flex flex-col gap-5"
          >
            {/* Hidden email target */}
            <input type="hidden" name="to_email" value="joshuasmithart@gmail.com" />

            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs tracking-widest uppercase text-stone-500 font-light">
                  Name <span className="text-stone-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="Your name"
                  className="border border-stone-200 px-3 py-2.5 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-stone-500 transition-colors bg-stone-50"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs tracking-widest uppercase text-stone-500 font-light">
                  Email <span className="text-stone-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  className="border border-stone-200 px-3 py-2.5 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-stone-500 transition-colors bg-stone-50"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-xs tracking-widest uppercase text-stone-500 font-light">
                Subject
              </label>
              <select
                name="subject"
                className="border border-stone-200 px-3 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-stone-500 transition-colors bg-stone-50 cursor-pointer"
              >
                <option value="General Inquiry">General Inquiry</option>
                <option value="Artwork Purchase">Artwork Purchase</option>
                <option value="Exhibition Request">Exhibition Request</option>
                <option value="Commission Request">Commission Request</option>
                <option value="Press & Media">Press &amp; Media</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <div className="flex justify-between items-center">
                <label className="text-xs tracking-widest uppercase text-stone-500 font-light">
                  Message <span className="text-stone-400">*</span>
                </label>
                <span className={`text-xs ${charCount > 500 ? 'text-red-400' : 'text-stone-300'}`}>
                  {charCount}/500
                </span>
              </div>
              <textarea
                name="message"
                required
                rows={5}
                maxLength={500}
                placeholder="Write your message here..."
                onChange={e => setCharCount(e.target.value.length)}
                className="border border-stone-200 px-3 py-2.5 text-sm text-stone-800 placeholder-stone-300 focus:outline-none focus:border-stone-500 transition-colors bg-stone-50 resize-none"
              />
              {charCount > 500 && (
                <p className="text-xs text-red-400">Message cannot exceed 500 characters.</p>
              )}
            </div>

            {status === 'error' && (
              <p className="text-xs text-red-500 tracking-wide">
                Something went wrong. Please try again.
              </p>
            )}

            <div className="flex items-center justify-between pt-2 border-t border-stone-100">
              <p className="text-xs text-stone-400 tracking-wide">
                Replies sent to joshuasmithart@gmail.com
              </p>
              <button
                type="submit"
                disabled={status === 'sending' || charCount > 500}
                className="px-8 py-3 bg-stone-900 text-white text-xs tracking-widest uppercase font-light hover:bg-stone-700 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {status === 'sending' ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
